import React, {useContext, useEffect, useRef, useState} from 'react';
import './StoenProjectRow.scss';
import {I18nContext} from '../../contexts/i18nContext';
import {constructClassNames, formatValueAsCurrency, getUserFullName} from '../../helpers/helpers';
import ConfirmationModal from '../_common/ConfirmationModal';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import EditCalendarIcon from "@mui/icons-material/EditCalendar";

const StoenProjectRow = (
  {
    project,
    users,
    openEditProjectModal,
    openDeleteProjectModal,
    openPreviewProjectModal,
    finishProject,
    cancelProject,
    reactivateProject,
    ordinalNumber,
    toggleSelectProject,
    isSelected,
    toggleMarkProjectAsUrgent,
    toggleMarkProjectAsToBePlanned
  }
) => {
  const { t } = useContext(I18nContext);
  const [showMeatballOptions, setShowMeatballOptions] = useState(false);
  const [
    showProjectFinishedStateChangeConfirmationModal,
    setShowProjectFinishedStateChangeConfirmationModal
  ] = useState(false);
  const [
    showProjectCanceledStateChangeConfirmationModal,
    setShowProjectCanceledStateChangeConfirmationModal
  ] = useState(false);

  const useOutsideAlerter = ref => {
    useEffect(() => {
      const handleClickOutside = event => {
        if (ref.current && !ref.current.contains(event.target)) {
          setShowMeatballOptions(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  const selectRef = useRef(null);
  useOutsideAlerter(selectRef);

  const toggleShowMeatballOptions = () => {
    setShowMeatballOptions(!showMeatballOptions);
  };

  const handleEditClick = project => {
    openEditProjectModal(project);
    toggleShowMeatballOptions();
  };

  const handleDeleteClick = project => {
    openDeleteProjectModal(project);
    toggleShowMeatballOptions();
  };

  const handlePreviewClick = project => {
    openPreviewProjectModal(project);
    toggleShowMeatballOptions();
  };

  const toggleShowFinishedStateChangeModal = () => {
    setShowProjectFinishedStateChangeConfirmationModal(!showProjectFinishedStateChangeConfirmationModal);
    toggleShowMeatballOptions();
  };

  const toggleShowCanceledStateChangeModal = () => {
    setShowProjectCanceledStateChangeConfirmationModal(!showProjectCanceledStateChangeConfirmationModal);
    toggleShowMeatballOptions();
  };

  const changeProjectFinishedState = () => {
    if (project.archived) {
      reactivateProject(project.id);
    } else {
      finishProject(project.id);
    }
    setShowProjectFinishedStateChangeConfirmationModal(false);
  };

  const changeProjectCanceledState = () => {
    if (project.archived) {
      reactivateProject(project.id);
    } else {
      cancelProject(project.id);
    }
    setShowProjectCanceledStateChangeConfirmationModal(false);
  };

  const projectRowClasses = [
    {name: 'StoenProjectRow--closeDue', condition: !project.archived && new Date(project.dueDate) - new Date() < 2629800000},
    {name: 'StoenProjectRow--overDue', condition: !project.archived && new Date(project.dueDate) - new Date() < 0}
  ];

  return (
    <div className={constructClassNames('StoenProjectRow', projectRowClasses)}>
      <div className='rowItemSelect'>
        <input
          className='projectSelectCheckbox'
          type='checkbox'
          checked={isSelected}
          onChange={() => toggleSelectProject(project.id)}
        />
      </div>
      <div className='rowItemMeatball' onClick={toggleShowMeatballOptions} ref={selectRef}>
        <div className='meatballDot'/>
        <div className='meatballDot'/>
        <div className='meatballDot'/>
      </div>
      <div className='rowItemUrgentMark'>
        {!!project?.urgent && (
          <PriorityHighIcon className='urgentIcon'/>
        )}
      </div>
      {/*<div className='rowItemPlanningMark'>*/}
      {/*  {!!project?.toBePlanned && (*/}
      {/*    <EditCalendarIcon className='toBePlannedIcon'/>*/}
      {/*  )}*/}
      {/*</div>*/}
      <div className='ordinalNumber'>
        {ordinalNumber}
      </div>
      <div className='details'>
        <div className='rowItem'>
          <div className='mobileLabel'>
            {t('investment address')}:
          </div>
          {project.investmentAddress}
        </div>
        <div className='rowItem'>
          <div className='mobileLabel'>
            {t('order number')}:
          </div>
          {project.orderNumber}
        </div>
        <div className='rowItem'>
          <div className='mobileLabel'>
            {t('conditions number')}:
          </div>
          {project.conditionsNumber}
        </div>
        <div className='rowItem'>
          <div className='mobileLabel'>
            {t('designer')}:
          </div>
          {project.designers.map(usr => getUserFullName(usr, users)).join(", ")}
        </div>
        <div className='rowItem'>
          <div className='mobileLabel'>
            {t('client name')}:
          </div>
          {project.clientContactDetails.fullName ?? '-'}
        </div>
        <div className='rowItem'>
          <div className='mobileLabel'>
            {t('due date')}:
          </div>
          {project.dueDate}
        </div>
        <div className='rowItem'>
          <div className='mobileLabel'>
            {t('created at')}:
          </div>
          {project.createdAt}
        </div>
        <div className='rowItem'>
          <div className='mobileLabel'>
            {t('last modified')}:
          </div>
          {project.lastModificationDate}
        </div>
        <div className='rowItem'>
          <div className='mobileLabel'>
            {t('order cost')}:
          </div>
          {formatValueAsCurrency(project.cost)}
        </div>
        <div className='rowItem'>
          <div className='mobileLabel'>
            {t('project state')}:
          </div>
          {project.projectState === 'completed' ? t('finished') : t(project.projectState)}
        </div>
      </div>
      {!!showMeatballOptions && (
        <div className='meatballOptions' ref={selectRef}>
          <div className='meatballOption' onClick={() => handlePreviewClick(project)}>{t('preview')}</div>
          {!project.archived && (
            <>
              <div className='meatballOption' onClick={() => handleEditClick(project)}>{t('edit')}</div>
              <div className='meatballOption' onClick={toggleShowFinishedStateChangeModal}>{t('finish')}</div>
              <div className='meatballOption' onClick={toggleShowCanceledStateChangeModal}>{t('cancel')}</div>
            </>
          )}
          {!!project.archived && (
            <div className='meatballOption' onClick={toggleShowCanceledStateChangeModal}>{t('reactivate')}</div>
          )}
          <div className='meatballOption' onClick={() => handleDeleteClick(project)}>{t('delete')}</div>
          {!project.archived && (
            <>
              <div className='meatballOption' onClick={() => toggleMarkProjectAsUrgent(project.id, !project.urgent)}>
                {t(project.urgent ? 'unmark urgent' : 'mark urgent')}
              </div>
              {/*<div*/}
              {/*  className='meatballOption'*/}
              {/*  onClick={() => toggleMarkProjectAsToBePlanned(project.id, !project.toBePlanned)}*/}
              {/*>*/}
              {/*  {t(project.toBePlanned ? 'remove from planning' : 'add to planning')}*/}
              {/*</div>*/}
            </>
          )}
        </div>
      )}
      {showProjectFinishedStateChangeConfirmationModal && (
        <ConfirmationModal
          primaryButtonAction={changeProjectFinishedState}
          title={!project.archived ? t('finish project') : t('reactivate project')}
          closeModal={() => setShowProjectFinishedStateChangeConfirmationModal(false)}
          itemName={project.investmentAddress}
          primaryButtonLabel={!project.archived ? t('finish project') : t('reactivate project')}
          modalText={!project.archived
            ? t('are you sure you want to finish project')
            : t('are you sure you want to reactivate project')
          }
        />
      )}
      {showProjectCanceledStateChangeConfirmationModal && (
        <ConfirmationModal
          primaryButtonAction={changeProjectCanceledState}
          title={!project.archived ? t('cancel project') : t('reactivate project')}
          closeModal={() => setShowProjectCanceledStateChangeConfirmationModal(false)}
          itemName={project.investmentAddress}
          primaryButtonLabel={!project.archived ? t('cancel project') : t('reactivate project')}
          modalText={!project.archived
            ? t('are you sure you want to cancel project')
            : t('are you sure you want to reactivate project')
          }
        />
      )}
    </div>
  );
};

export default StoenProjectRow;
